import { Card } from '../design-system/Card';
import { Text } from '../design-system/Text';
import { Button } from '../design-system/Button';
import { useNudgeImpression } from '../../hooks/useNudgeImpression';
import { analytics } from '../../lib/analytics';
import { PartyPopper, Wallet, X } from 'lucide-react';
import { formatCurrency } from '../../utils/format';
import type { FastagProfile } from '../../types';

interface NudgePostPaymentCardProps {
  profile: FastagProfile;
  visible: boolean;
  onRecharge: () => void;
  onDismiss: () => void;
  dismissed: boolean;
}

export function NudgePostPaymentCard({
  profile,
  visible,
  onRecharge,
  onDismiss,
  dismissed,
}: NudgePostPaymentCardProps) {
  useNudgeImpression('nudge_post_payment_card', visible && !dismissed, { balance: profile.balance });

  if (!visible || dismissed) return null;

  const handleRecharge = () => {
    analytics.emit('recharge_cta_click', { nudge_id: 'nudge_post_payment_card', balance: profile.balance });
    onRecharge();
  };

  const handleDismiss = () => {
    analytics.emit('nudge_dismiss', { nudge_id: 'nudge_post_payment_card' });
    onDismiss();
  };

  return (
    <Card
      padding="md"
      elevation={3}
      className="mx-4 mt-4 border-park-200 bg-gradient-to-br from-park-50 to-white dark:border-park-800 dark:from-park-900/20 dark:to-slate-800"
      data-testid="nudge-post-payment"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-park-100 text-park-600 dark:bg-park-900/40 dark:text-park-400">
          <PartyPopper className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <Text variant="title">Challans paid! Keep FASTag ready</Text>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss post payment nudge"
              className="shrink-0 rounded-md p-1 text-slate-400 hover:text-slate-600 focus-visible:ring-2 touch-target"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <Text variant="body" color="secondary" className="mt-1">
            Your FASTag balance is {formatCurrency(profile.balance)}. Add {formatCurrency(profile.recommendedAmount)} now so your next toll is seamless.
          </Text>
          <div className="mt-4 flex items-center gap-3">
            <Button onClick={handleRecharge} aria-label="Recharge FASTag now">
              <Wallet className="h-4 w-4" />
              Recharge {formatCurrency(profile.recommendedAmount)}
            </Button>
            <Button variant="ghost" onClick={handleDismiss}>Maybe later</Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
